import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { useToast } from '../../contexts/ToastContext'
import IvideonSettings from '../../components/IvideonSettings/IvideonSettings'
import { IconSettings, IconUser, IconPalette, IconShield, IconUsers, IconWallet, IconBot, IconCamera, IconLock, IconBell } from '../../components/Icons'

// Импорт новых подкомпонентов
import ProfileSettings from './components/ProfileSettings'
import AppearanceSettings from './components/AppearanceSettings'
import AccountSettings from './components/AccountSettings'
import TeamSettings from './components/TeamSettings'
import SalarySettings from './components/SalarySettings'
import AiSettings from './components/AiSettings'
import PermissionsSettings from './components/PermissionsSettings'
import NotificationsSettings from './components/NotificationsSettings'
import { getPermission } from '../../lib/permissionDefaults'
import { getSocket } from '../../lib/socketClient'

import './SettingsPage.css'

export default function SettingsPage({ onPageChange, initialTab, pageParams, onTabConsumed }) {
  const { user, profile, setProfile, isAdmin, isOwner, signOut, userPvzs, activePvz, setActivePvz, rolePermissions: globalRolePermissions, adminPermissions } = useAuth()
  const toast = useToast()
  
  const [activeTheme, setActiveTheme] = useState(profile?.theme || 'light')
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem('wb_settings_tab')
      if (saved) {
        localStorage.removeItem('wb_settings_tab')
        return saved
      }
    } catch (e) {}
    return initialTab || 'profile'
  })
  
  // Состояние команды
  const [users, setUsers] = useState([])
  const [loadingTeam, setLoadingTeam] = useState(false)

  // Состояние настроек ЗП
  const [salarySettings, setSalarySettings] = useState({
    dailyRate: 1700,
    bonusPerAccepted: 1,
    bonusPerIssued: 1.5,
    helperHourlyRate: 100,
    helperDeductionPercent: 50,
    customRules: [],
    wbRevenuePerIssued: 15.0,
  })
  const [salaryDirty, setSalaryDirty] = useState(false)
  const [salarySaving, setSalarySaving] = useState(false)
  const [salaryLoading, setSalaryLoading] = useState(false)

  // Состояние настроек ИИ
  const [aiSettings, setAiSettings] = useState({ provider: 'gemini', api_key: '' })
  const [aiLoading, setAiLoading] = useState(false)
  const [aiSaving, setAiSaving] = useState(false)
  const [confirmDeleteKey, setConfirmDeleteKey] = useState(false)

  // Состояние матрицы прав
  const [localRolePermissions, setLocalRolePermissions] = useState(globalRolePermissions || { admin: {}, employee: {}, helper: {} })

  useEffect(() => {
    setLocalRolePermissions(globalRolePermissions || { admin: {}, employee: {}, helper: {} })
  }, [globalRolePermissions])

  // Если передан initialTab (напр. из баннера на дашборде) — применяем и сбрасываем
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab)
      onTabConsumed?.()
    }
  }, [initialTab])

  // Слушатель смены вкладки при открытой странице настроек (из deep-link уведомлений)
  useEffect(() => {
    const handleSettingsTab = (e) => {
      const tab = e?.detail?.tab || localStorage.getItem('wb_settings_tab')
      if (tab) {
        setActiveTab(tab)
        try { localStorage.removeItem('wb_settings_tab') } catch (err) {}
      }
    }
    window.addEventListener('settings-tab-navigate', handleSettingsTab)
    return () => window.removeEventListener('settings-tab-navigate', handleSettingsTab)
  }, [])

  const loadTeam = async (silent = false) => {
    if (!profile?.org_id) return
    if (!silent) setLoadingTeam(true)
    try {
      const data = await api.getTeamMembers(profile.org_id)
      setUsers(data)
    } catch (err) {
      toast.error('Ошибка загрузки команды')
    } finally {
      setLoadingTeam(false)
    }
  }

  // Загрузка команды
  useEffect(() => {
    if (activeTab === 'team' && profile?.org_id) {
      loadTeam()
    }
  }, [activeTab, profile?.org_id])

  // Автоматическая тихая загрузка команды при входе на страницу настроек для отображения бейджа
  useEffect(() => {
    if (profile?.org_id && getPermission(adminPermissions, 'canManageTeam', profile?.role)) {
      loadTeam(true)
    }
  }, [profile?.org_id])

  // WS подписка на новые регистрации
  useEffect(() => {
    const socket = getSocket()
    if (socket && profile?.org_id) {
      const handleNewReg = (newUser) => {
        console.log('[WS] New employee registration:', newUser)
        if (profile?.role === 'owner') {
          loadTeam(true)
        }
      }
      
      socket.on('new-registration', handleNewReg)
      return () => {
        socket.off('new-registration', handleNewReg)
      }
    }
  }, [profile?.org_id])

  // Загрузка настроек ЗП
  useEffect(() => {
    if (isAdmin && activeTab === 'salary') {
      setSalaryLoading(true)
      api.getSalaryConfig(activePvz?.id)
        .then((config) => {
          setSalarySettings(config)
          setSalaryDirty(false)
        })
        .catch(console.error)
        .finally(() => setSalaryLoading(false))
    }
  }, [isAdmin, activeTab, activePvz?.id])

  // Загрузка настроек ИИ
  useEffect(() => {
    if (isOwner && activeTab === 'ai') {
      setAiLoading(true)
      api.getAISettings(profile?.org_id)
        .then(settings => setAiSettings(settings || { provider: 'gemini', api_key: '' }))
        .catch(console.error)
        .finally(() => setAiLoading(false))
    }
  }, [isOwner, activeTab, profile?.org_id])

  const handleThemeChange = (themeId, e) => {
    const themeName = themeId === 'wb' ? 'light' : themeId

    const changeLogic = () => {
      setActiveTheme(themeId)
      document.documentElement.setAttribute('data-theme', themeName === 'light' ? '' : themeName)
      localStorage.setItem('theme', themeName)
      api.updateProfile(profile.id, { theme: themeId }).catch(console.error)
    }

    const x = e?.clientX || window.innerWidth / 2
    const y = e?.clientY || window.innerHeight / 2
    
    document.documentElement.style.setProperty('--x', `${x}px`)
    document.documentElement.style.setProperty('--y', `${y}px`)

    if (!document.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      changeLogic()
      return
    }

    document.startViewTransition(() => {
      changeLogic()
    })
  }

  const handleSaveSalarySettings = async (effectiveDate) => {
    setSalarySaving(true)
    try {
      let res
      if (effectiveDate && typeof effectiveDate === 'string') {
        res = await api.saveSalaryConfigWithDate(salarySettings, effectiveDate, profile.id)
      } else {
        res = await api.updateSalaryConfig(salarySettings, profile.id)
      }
      setSalaryDirty(false)
      if (res && res.partial) {
        toast.warning('Настройки сохранены частично. Обратитесь к администратору для применения миграции 031 в базе данных.')
      } else {
        toast.success(`Настройки зарплаты успешно сохранены${effectiveDate && typeof effectiveDate === 'string' ? ' с ' + new Date(effectiveDate).toLocaleDateString('ru-RU') : ''}!`)
      }
    } catch (err) {
      console.error('Ошибка сохранения ЗП:', err)
      toast.error(`Ошибка при сохранении: ${err.message}`)
    } finally {
      setSalarySaving(false)
    }
  }

  const handleSaveAiSettings = async (e) => {
    if (e) e.preventDefault()
    setAiSaving(true)
    try {
      await api.updateAISettings(profile?.org_id, aiSettings)
      toast.success('Настройки ИИ успешно сохранены!')
    } catch (err) {
      console.error('Ошибка сохранения ИИ:', err)
      toast.error(`Ошибка при сохранении: ${err.message}`)
    } finally {
      setAiSaving(false)
    }
  }

  const handleDeleteKey = async () => {
    if (!confirmDeleteKey) {
      setConfirmDeleteKey(true)
      setTimeout(() => setConfirmDeleteKey(false), 3000)
      return
    }
    setAiSaving(true)
    setConfirmDeleteKey(false)
    try {
      await api.saveAiSettings({ provider: aiSettings.provider, api_key: '' })
      setAiSettings({ ...aiSettings, api_key: '' })
      toast.success('Ключ удалён')
    } catch (err) {
      toast.error('Ошибка: ' + err.message)
    } finally {
      setAiSaving(false)
    }
  }

  const pendingUsers = users.filter(u => !u.is_approved && !u.full_name?.startsWith('REJECTED_') && !u.full_name?.startsWith('BLOCKED_') && !u.is_legacy)

  return (
    <div className="settings-page animate-fade-in">
      <div className="settings-header">
        <div className="settings-header-icon-wrap">
          <IconSettings size={28} />
        </div>
        <div>
          <h1 className="settings-title">Настройки</h1>
          <p className="settings-subtitle">Управление профилем и приложением</p>
        </div>
      </div>

      {/* Вкладки */}
      <div className="settings-tabs" data-tour="settings-general" onTouchMove={e => e.stopPropagation()}>
        <button
          className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <IconUser size={16} /> Профиль
        </button>
        <button
          className={`settings-tab ${activeTab === 'appearance' ? 'active' : ''}`}
          onClick={() => setActiveTab('appearance')}
        >
          <IconPalette size={16} /> Внешний вид
        </button>
        <button
          className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          <IconBell size={16} /> Уведомления
        </button>
        <button
          className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`}
          onClick={() => setActiveTab('account')}
        >
          <IconShield size={16} /> Аккаунт
        </button>
        {getPermission(adminPermissions, 'canManageTeam', profile?.role) && (
          <button
            className={`settings-tab ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => setActiveTab('team')}
            data-tour="settings-team-tab"
          >
            <IconUsers size={16} /> Команда
            {pendingUsers.length > 0 && (
              <span className="badge badge-danger" style={{ marginLeft: 6 }}>
                {pendingUsers.length}
              </span>
            )}
          </button>
        )}
        {getPermission(adminPermissions, 'canManageSalaryConfig', profile?.role) && (
          <button
            className={`settings-tab ${activeTab === 'salary' ? 'active' : ''}`}
            onClick={() => setActiveTab('salary')}
            data-tour="settings-salary-tab"
          >
            <IconWallet size={16} /> Расчёт ЗП
          </button>
        )}
        {isOwner && (
          <button
            className={`settings-tab ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai')}
          >
            <IconBot size={16} /> ИИ Агент
          </button>
        )}
        {isOwner && (
          <button
            className={`settings-tab ${activeTab === 'cameras' ? 'active' : ''}`}
            onClick={() => setActiveTab('cameras')}
          >
            <IconCamera size={16} /> Камеры
          </button>
        )}
        {isOwner && (
          <button
            className={`settings-tab ${activeTab === 'permissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('permissions')}
          >
            <IconLock size={16} /> Права доступа
          </button>
        )}
      </div>

      {/* Содержимое вкладок */}
      {activeTab === 'profile' && (
        <ProfileSettings
          profile={profile}
          user={user}
          isAdmin={isAdmin}
          isOwner={isOwner}
          canManageGeneralSettings={getPermission(adminPermissions, 'canManageGeneralSettings', profile?.role)}
          signOut={signOut}
          userPvzs={userPvzs}
          setActivePvz={setActivePvz}
        />
      )}

      {activeTab === 'appearance' && (
        <AppearanceSettings
          activeTheme={activeTheme}
          handleThemeChange={handleThemeChange}
        />
      )}

      {activeTab === 'notifications' && (
        <NotificationsSettings
          profile={profile}
          setProfile={setProfile}
        />
      )}

      {activeTab === 'account' && (
        <AccountSettings
          profile={profile}
          user={user}
        />
      )}

      {activeTab === 'team' && getPermission(adminPermissions, 'canManageTeam', profile?.role) && (
        <TeamSettings
          users={users}
          setUsers={setUsers}
          loadingTeam={loadingTeam}
          isOwner={isOwner}
          isAdmin={isAdmin}
          profile={profile}
          setProfile={setProfile}
          user={user}
          loadTeam={loadTeam}
          userPvzs={userPvzs}
        />
      )}

      {activeTab === 'salary' && getPermission(adminPermissions, 'canManageSalaryConfig', profile?.role) && (
        <SalarySettings
          salaryLoading={salaryLoading}
          salarySettings={salarySettings}
          setSalarySettings={setSalarySettings}
          salaryDirty={salaryDirty}
          setSalaryDirty={setSalaryDirty}
          salarySaving={salarySaving}
          setSalarySaving={setSalarySaving}
          handleSaveSalarySettings={handleSaveSalarySettings}
          profile={profile}
        />
      )}

      {activeTab === 'ai' && isOwner && (
        <AiSettings
          aiLoading={aiLoading}
          aiSettings={aiSettings}
          setAiSettings={setAiSettings}
          aiSaving={aiSaving}
          handleSaveAiSettings={handleSaveAiSettings}
          confirmDeleteKey={confirmDeleteKey}
          setConfirmDeleteKey={setConfirmDeleteKey}
          handleDeleteKey={handleDeleteKey}
        />
      )}

      {activeTab === 'permissions' && isOwner && (
        <PermissionsSettings
          localRolePermissions={localRolePermissions}
          setLocalRolePermissions={setLocalRolePermissions}
          profile={profile}
        />
      )}

      {activeTab === 'cameras' && isOwner && (
        <IvideonSettings 
          oauthCode={pageParams?.ivideonOAuthCode}
          oauthState={pageParams?.ivideonOAuthState}
        />
      )}
    </div>
  )
}
